const backdropReaders = new WeakMap();

// WebGLの描画直後だけ読む。描画バッファの保持や既存の描画設定は変えない。
export const notifyBrandBackdrop = (canvas, complete = false) => backdropReaders.get(canvas)?.(complete);

// 白だけを対象にする。縮小描画時の丸め誤差として各チャンネル1階調だけ許容する。
export const useDarkLogo = (rgb) => rgb.every((channel) => channel >= 254);

export function mediaPoint(point, box, width, height, fit = 'fill', position = '50% 50%') {
  if (!box.width || !box.height || !width || !height) return null;
  let x = (point.x - box.left) / box.width;
  let y = (point.y - box.top) / box.height;
  if (fit === 'cover' || fit === 'contain') {
    const scale = Math[fit === 'cover' ? 'max' : 'min'](box.width / width, box.height / height);
    const offsets = position.split(/\s+/).map((value) => parseFloat(value) / 100);
    x = (point.x - box.left - (box.width - width * scale) * offsets[0]) / (width * scale);
    y = (point.y - box.top - (box.height - height * scale) * (offsets[1] ?? offsets[0])) / (height * scale);
  }
  return x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : null;
}

function color(value) {
  const values = value.match(/[\d.]+/g)?.map(Number);
  return values?.length >= 3 ? [...values.slice(0, 3), values[3] ?? 1] : [0, 0, 0, 0];
}

export function composite(layers) {
  const result = [0, 0, 0];
  let remaining = 1;
  for (const [r, g, b, alpha] of layers) {
    [r, g, b].forEach((channel, index) => { result[index] += channel * alpha * remaining; });
    remaining *= 1 - alpha;
  }
  return result.map((channel) => channel + 255 * remaining);
}

export function initBrandTone(brand, { scroller, canvases = [], revealActivity }) {
  const document = brand.ownerDocument;
  const view = document.defaultView;
  const sampler = document.createElement('canvas');
  const rasters = new WeakMap();
  const attempts = new WeakMap();
  const cleanups = [];
  const size = 64;
  let frame = 0;
  let destroyed = false;
  let brandBox = brand.getBoundingClientRect();
  const listen = (element, type, callback, options) => {
    element.addEventListener(type, callback, options);
    cleanups.push(() => element.removeEventListener(type, callback, options));
  };
  const snapshot = (media) => {
    try {
      sampler.width = sampler.height = size;
      const context = sampler.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.drawImage(media, 0, 0, size, size);
      const raster = { src: media.currentSrc, data: context.getImageData(0, 0, size, size).data };
      rasters.set(media, raster);
      return raster;
    } catch { return null; }
  };
  const pixel = (media, point, style) => {
    let raster = rasters.get(media);
    if (media.tagName === 'IMG' && media.complete && media.naturalWidth && (!raster || raster.src !== media.currentSrc)) raster = snapshot(media);
    if (media.tagName === 'VIDEO' && media.readyState >= 2 && (!raster || view.performance.now() - (attempts.get(media) ?? -Infinity) >= 100)) {
      attempts.set(media, view.performance.now());
      raster = snapshot(media);
    }
    if (!raster) return null;
    const location = mediaPoint(point, media.getBoundingClientRect(), media.naturalWidth || media.videoWidth || media.width, media.naturalHeight || media.videoHeight || media.height, style.objectFit, style.objectPosition);
    if (!location) return null;
    const offset = (Math.min(size - 1, Math.floor(location.y * size)) * size + Math.min(size - 1, Math.floor(location.x * size))) * 4;
    return [...raster.data.slice(offset, offset + 3), raster.data[offset + 3] / 255];
  };
  const update = () => {
    frame = 0;
    if (destroyed || document.hidden || revealActivity?.active) return;
    brandBox = brand.getBoundingClientRect();
    if (!brandBox.width || !brandBox.height) return;
    const styles = new WeakMap();
    const opacity = new WeakMap();
    const styleOf = (element) => {
      if (!styles.has(element)) styles.set(element, view.getComputedStyle(element));
      return styles.get(element);
    };
    const opacityOf = (element) => {
      if (!element) return 1;
      if (!opacity.has(element)) opacity.set(element, Number(styleOf(element).opacity) * opacityOf(element.parentElement));
      return opacity.get(element);
    };
    const at = (point) => {
      const layers = [];
      for (const element of document.elementsFromPoint(point.x, point.y)) {
        if (brand.contains(element)) continue;
        const style = styleOf(element);
        const alpha = opacityOf(element);
        if (!alpha || style.visibility === 'hidden' || style.display === 'none') continue;
        const add = (rgba) => { if (rgba) layers.push([rgba[0], rgba[1], rgba[2], rgba[3] * alpha]); };
        const submv = element.closest('.submv');
        if (submv && (element === submv || (submv.matches('.is-webgl') && element.closest('.backdrop')))) {
          const canvas = submv.matches('.is-webgl') ? submv.querySelector('.flyeye') : null;
          let rgba = canvas && Number(styleOf(canvas).opacity) ? pixel(canvas, point, styleOf(canvas)) : null;
          if (!rgba) {
            const image = submv.querySelector('.swiper-slide-active img') || submv.querySelector('.backdrop img');
            if (image) rgba = pixel(image, point, styleOf(image));
          }
          add(rgba);
        }
        if (element.matches('.rail-intro')) {
          const canvas = element.querySelector('.reserve-gradient.is-ready canvas');
          if (canvas && styleOf(canvas).visibility !== 'hidden') add(pixel(canvas, point, styleOf(canvas)));
        }
        if (['IMG', 'VIDEO'].includes(element.tagName)) add(pixel(element, point, style));
        if (style.backgroundImage?.startsWith('linear-gradient')) {
          const percent = parseFloat(style.backgroundSize);
          const box = element.getBoundingClientRect();
          if (!Number.isFinite(percent) || point.x <= box.left + box.width * percent / 100) add(color(style.backgroundImage.match(/rgba?\([^)]+\)/)?.[0] ?? ''));
        }
        add(color(style.backgroundColor));
        if (layers.some((layer) => layer[3] >= 1)) break;
      }
      return composite(layers);
    };
    const samples = [];
    for (const x of [.2, .5, .8]) for (const y of [.2, .5, .8]) samples.push(at({ x: brandBox.left + brandBox.width * x, y: brandBox.top + brandBox.height * y }));
    brand.classList.toggle('is-dark', samples.every(useDarkLogo));
  };
  const requestUpdate = () => {
    if (!frame && !destroyed && !revealActivity?.active) frame = view.requestAnimationFrame(update);
  };
  for (const canvas of canvases) {
    const capture = (complete) => {
      const now = view.performance.now();
      if (revealActivity?.active || document.hidden || (!complete && now - (attempts.get(canvas) ?? -Infinity) < 100)) return;
      attempts.set(canvas, now);
      const box = canvas.getBoundingClientRect();
      if (box.right <= brandBox.left || box.left >= brandBox.right || box.bottom <= brandBox.top || box.top >= brandBox.bottom) { rasters.delete(canvas); return; }
      snapshot(canvas);
      requestUpdate();
    };
    backdropReaders.set(canvas, capture);
    cleanups.push(() => backdropReaders.delete(canvas));
  }
  listen(scroller, 'scroll', requestUpdate, { passive: true });
  listen(view, 'resize', requestUpdate);
  listen(document, 'load', requestUpdate, true);
  listen(document, 'animationend', requestUpdate);
  listen(document, 'transitionend', requestUpdate);
  listen(document, 'visibilitychange', requestUpdate);
  document.querySelectorAll('video').forEach((video) => listen(video, 'timeupdate', requestUpdate));
  update();
  if (revealActivity) cleanups.push(revealActivity.subscribe((active) => {
    if (active) { view.cancelAnimationFrame(frame); frame = 0; }
    else requestUpdate();
  }));
  return () => {
    destroyed = true;
    view.cancelAnimationFrame(frame);
    cleanups.forEach((cleanup) => cleanup());
    brand.classList.remove('is-dark');
  };
}
